import { getAuthService } from '~/apps/auth/service'
import { useAuthStore } from '~/stores/useAuthStore'
import type { GoogleCredentialResponse } from '~/types/google-signin'

export const useGoogleSignIn = () => {
  const authStore = useAuthStore()
  const { $loadScriptWithPromise } = useNuxtApp()

  // Global state for Google Sign-In - must be inside the composable
  const globalGoogleState = useState<{
    isInitialized: boolean
    isInitializing: boolean
    isScriptLoaded: boolean
    scriptLoadFailed: boolean
  }>('googleSignInState', () => ({
    isInitialized: false,
    isInitializing: false,
    isScriptLoaded: false,
    scriptLoadFailed: false,
  }))

  async function waitForAuth() {
    if (authStore.isLoggedIn) return
    await authStore.authCheck
  }

  function handleCredentialResponse(response: GoogleCredentialResponse) {
    try {
      if (!response.credential) {
        return
      }

      // First ensure we're logged out
      authStore.logOutInPlace()

      // Get current route to check for next parameter
      const route = useRoute()

      // Extract next parameter from query, fallback to current page
      const next = (route.query.next as string) || route.fullPath

      // Send Google token to auth service with next param
      const authService = getAuthService()
      authService.send('LOG_IN_GOOGLE', {
        token: response.credential,
        next,
      })
    } catch (error) {
      console.error('Error handling Google credential response:', error)
    }
  }

  async function loadGoogleScript() {
    if (globalGoogleState.value.isScriptLoaded) {
      return true
    }

    try {
      // Remove any existing script tag that may have failed to load
      // This ensures $loadScriptWithPromise will create a fresh script tag
      if (import.meta.client) {
        const existingScript = document.getElementById('google-signin')
        if (existingScript) {
          existingScript.remove()
        }
      }

      // Create a timeout promise
      // 10 seconds should be enough even on slow connections
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Script loading timed out')), 10000)
      })

      // Race between script loading and timeout
      await Promise.race([
        $loadScriptWithPromise(
          'https://accounts.google.com/gsi/client',
          'google-signin',
          { 'data-cookieyes': 'cookieyes-functional' },
        ),
        timeoutPromise,
      ])

      if (!window.google?.accounts?.id) {
        return false
      }

      globalGoogleState.value.isScriptLoaded = true
      globalGoogleState.value.scriptLoadFailed = false
      return true
    } catch (error) {
      globalGoogleState.value.scriptLoadFailed = true
      console.error('Error loading Google Sign-In script:', error)
      return false
    }
  }

  // Set up CookieYes consent listener
  function setupConsentListener() {
    // Check if listener is already set up
    const listenerKey = 'googleSignInConsentListenerSet'
    if (useState(listenerKey).value) {
      return
    }

    // Mark listener as set up
    useState(listenerKey).value = true

    // Listen for CookieYes consent updates
    if (import.meta.client) {
      document.addEventListener('cookieyes_consent_update', () => {
        // Always attempt initialization on consent update
        // The Google button works even if cookies are rejected, just needs a retry
        // Force re-attempt script load by resetting all state flags
        // This ensures loadGoogleScript will remove stale script tags and retry
        globalGoogleState.value.isScriptLoaded = false
        globalGoogleState.value.isInitialized = false
        globalGoogleState.value.isInitializing = false
        globalGoogleState.value.scriptLoadFailed = false

        void initializeGoogleSignIn()
      })
    }
  }

  async function initializeGoogleSignIn() {
    // Set up consent listener on first call
    setupConsentListener()

    // Check if already initialized or currently initializing
    if (
      globalGoogleState.value.isInitialized ||
      globalGoogleState.value.isInitializing
    ) {
      return globalGoogleState.value.isInitialized
    }

    // Mark as initializing to prevent concurrent initialization
    globalGoogleState.value.isInitializing = true

    await waitForAuth()
    if (authStore.isLoggedIn) {
      globalGoogleState.value.isInitializing = false
      return false
    }

    try {
      // Get client ID from runtime config
      const config = useRuntimeConfig()
      const clientId = config.public.googleApiKey
      if (!clientId) {
        globalGoogleState.value.isInitializing = false
        return false
      }

      // Load script if needed
      const scriptLoaded = await loadGoogleScript()
      if (!scriptLoaded) {
        globalGoogleState.value.isInitializing = false
        globalGoogleState.value.scriptLoadFailed = true
        // Script failed to load - CookieYes might be blocking
        // The consent listener will retry on any consent update

        // Add a simple retry mechanism for temporary network issues
        setTimeout(() => {
          if (
            !globalGoogleState.value.isInitialized &&
            !globalGoogleState.value.isInitializing
          ) {
            void initializeGoogleSignIn()
          }
        }, 3000)

        return false
      }

      // Initialize Google Sign-In (only once globally)
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: false,
          context: 'signin',
          itp_support: true,
          use_fedcm_for_prompt: true,
        })
      } catch (initError) {
        console.error('[Google Sign-In] Initialization error:', {
          error:
            initError instanceof Error ? initError.message : String(initError),
          clientIdPresent: !!clientId,
          googleObjectPresent: !!window.google?.accounts?.id,
        })
        throw initError
      }

      // Mark as initialized
      globalGoogleState.value.isInitialized = true
      globalGoogleState.value.isInitializing = false
      globalGoogleState.value.scriptLoadFailed = false
      return true
    } catch (error) {
      globalGoogleState.value.isInitialized = false
      globalGoogleState.value.isInitializing = false
      globalGoogleState.value.scriptLoadFailed = true

      console.error('Error in Google Sign-In initialization:', error)
      return false
    }
  }

  return {
    initializeGoogleSignIn,
    loadGoogleScript,
    isInitialized: computed(() => globalGoogleState.value.isInitialized),
    isInitializing: computed(() => globalGoogleState.value.isInitializing),
    isScriptLoaded: computed(() => globalGoogleState.value.isScriptLoaded),
    scriptLoadFailed: computed(() => globalGoogleState.value.scriptLoadFailed),
    isLoggedIn: computed(() => authStore.isLoggedIn),
    // Expose google object check for components
    hasGoogleObject: () => !!window.google?.accounts?.id,
  }
}
